import type { AdminAction, CardPaymentsSummary, Row } from '@contract';
import { formatMoney } from '@sao/shared/money';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Fragment, type ReactNode, useEffect } from 'react';
import { z } from 'zod';
import { ExportButton } from '@/components/export-button';
import { PoolRefresh } from '@/components/home/pool-refresh';
import { BookCardSettlement } from '@/components/money/book-card-settlement';
import { CardsSummary } from '@/components/money/cards-summary';
import {
  fileMoneyRows,
  MONEY_SHEET_SCREENS,
  moneySheetRef,
} from '@/components/money/money-rows';
import { ResolveBreak } from '@/components/money/resolve-break';
import {
  ResolveStuckRow,
  ResolveSuspenseRow,
} from '@/components/money/resolve-stuck';
import {
  type SettlementFlow,
  SettlementSummary,
  type SettlementTotal,
  SettlementWarnings,
} from '@/components/money/settlement-summary';
import { SweepAuto } from '@/components/money/sweep-auto';
import { SweepPolicyView } from '@/components/money/sweep-policy';
import { SweepReports } from '@/components/money/sweep-reports';
import { SweepsPanel } from '@/components/money/sweeps-panel';
import { PageHeader } from '@/components/page-header';
import { ProposeChange } from '@/components/propose-change';
import { type ColumnSpec, RecordTable } from '@/components/record-table';
import { TabLinks } from '@/components/tab-links';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ageMs, durationText, type OpsStatus } from '@/lib/home-model';
import { hasHubtel, homeCurrency } from '@/lib/market';
import { adminQuery, useKeysetList } from '@/lib/query';
import type { SheetRef } from '@/lib/sheet-url';

/** A table column; the screens below read as one line per column. */
function col(
  key: string,
  label: string,
  kind?: ColumnSpec['kind'],
): ColumnSpec {
  return { key, label, kind };
}

export interface Screen {
  key: string;
  label: string;
  action: AdminAction;
  description: string;
  columns: ColumnSpec[];
  /** The fields the record sheet shows, when they differ from the columns. */
  sheetFields?: ColumnSpec[];
  /** The row key holding a JSON detail the sheet lays out as pairs. */
  detailKey?: string;
  /** What a staff member can propose about one row. */
  rowActions?: (row: Row) => ReactNode;
  /** The filter fields this screen offers, read from the URL. */
  filters?: readonly (
    | 'from'
    | 'to'
    | 'day'
    | 'status'
    | 'kind'
    | 'source'
    | 'class'
    | 'unbooked'
    | 'issues_only'
    | 'show'
    | 'card_status'
    | 'unsettled'
    | 'show_test'
  )[];
  /** What the table says when it has no rows. */
  empty?: string;
  /** The kinds to pick from, when the kind filter is a list rather than text. */
  kinds?: readonly { value: string; label: string }[];
}

export const SCREENS: readonly Screen[] = [
  {
    key: 'breaks',
    label: 'Breaks',
    action: 'money.breaks',
    description:
      'Every reconciliation break. An open pool or books break holds payouts until it is resolved.',
    filters: ['status', 'kind'],
    columns: [
      { key: 'opened_at', label: 'Opened', kind: 'datetime' },
      { key: 'kind', label: 'Problem', kind: 'words' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'holds_payouts', label: 'Holds payouts', kind: 'bool' },
      { key: 'customer_id', label: 'Customer', kind: 'customer' },
    ],
    detailKey: 'detail',
    sheetFields: [
      { key: 'kind', label: 'Kind', kind: 'mono' },
      { key: 'opened_at', label: 'Opened', kind: 'datetime' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'holds_payouts', label: 'Holds payouts', kind: 'bool' },
      { key: 'customer_id', label: 'Customer', kind: 'customer' },
      { key: 'transaction_id', label: 'Transaction', kind: 'transaction' },
      { key: 'provider', label: 'Provider' },
      { key: 'provider_ref', label: 'Reference', kind: 'mono' },
      { key: 'owner', label: 'Owner' },
      { key: 'resolved_at', label: 'Resolved', kind: 'datetime' },
    ],
    rowActions: (row) => <ResolveBreak row={row} />,
  },
  {
    key: 'stuck',
    label: 'Stuck',
    action: 'money.stuck',
    description:
      'Transfers, collections, payouts and bills pending for more than 30 minutes or in review.',
    columns: [
      { key: 'since', label: 'Since', kind: 'datetime' },
      { key: 'kind', label: 'Kind' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'amount_minor', label: 'Amount', kind: 'money' },
      { key: 'provider', label: 'Provider' },
      { key: 'last_answer', label: 'Last answer' },
      { key: 'customer_id', label: 'Customer', kind: 'customer' },
      { key: 'transaction_id', label: 'Transaction', kind: 'transaction' },
    ],
    rowActions: (row) => <ResolveStuckRow row={row} />,
  },
  {
    key: 'reversals',
    label: 'Reversals',
    action: 'money.reversals',
    description:
      'Money given back to wallets, newest first: reversals, bills and payouts that left Sao and then failed (shown as "Hold released": the wallet was never credited, the hold on its money came off), and staff credits made for a transaction (corrections).',
    filters: ['source', 'kind', 'from', 'to'],
    kinds: [
      { value: 'transfer', label: 'Sao to Sao' },
      { value: 'bill', label: 'Bill' },
      { value: 'momo', label: 'Mobile money' },
      { value: 'bank', label: 'Bank' },
      { value: 'topup', label: 'Top-up' },
      { value: 'other', label: 'Other' },
    ],
    columns: [
      { key: 'at', label: 'When', kind: 'datetime' },
      { key: 'type', label: 'Type', kind: 'return-type' },
      { key: 'channel', label: 'Payment' },
      { key: 'wallet_number', label: 'Wallet', kind: 'mono' },
      { key: 'amount_minor', label: 'Returned', kind: 'money' },
      { key: 'fee_minor', label: 'Fee returned', kind: 'money' },
      { key: 'automatic', label: 'Automatic', kind: 'bool' },
      { key: 'by', label: 'By' },
      { key: 'reason', label: 'Reason' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'original_id', label: 'Original', kind: 'transaction' },
    ],
  },
  {
    key: 'suspense',
    label: 'Suspense',
    action: 'money.suspense',
    description:
      'Money held in suspense, oldest first, with its decide-by date.',
    columns: [
      { key: 'held_since', label: 'Held since', kind: 'datetime' },
      { key: 'held_days', label: 'Days', kind: 'number' },
      { key: 'overdue', label: 'Overdue', kind: 'bool' },
      { key: 'decide_by', label: 'Decide by', kind: 'datetime' },
      { key: 'amount_minor', label: 'Amount', kind: 'money' },
      { key: 'wallet_number', label: 'Wallet', kind: 'mono' },
      { key: 'source', label: 'Source' },
      { key: 'sender', label: 'Sender' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'transaction_id', label: 'Transaction', kind: 'transaction' },
    ],
    rowActions: (row) => <ResolveSuspenseRow row={row} />,
  },
  {
    key: 'pool',
    label: 'Pool checks',
    action: 'money.pool_checks',
    description:
      'The daily check that the pool account covers the wallets. Cover is what the bank holds above the wallets: funding and fee income.',
    filters: ['from', 'to'],
    columns: [
      { key: 'business_date', label: 'Day', kind: 'date' },
      { key: 'bank', label: 'Bank' },
      { key: 'account', label: 'Account', kind: 'mono' },
      { key: 'result', label: 'Result', kind: 'status' },
      { key: 'wallets_minor', label: 'Wallets total', kind: 'money' },
      { key: 'bank_balance_minor', label: 'Bank balance', kind: 'money' },
      { key: 'cover_minor', label: 'Cover', kind: 'money' },
      { key: 'pulls_in_flight_minor', label: 'Pulls in flight', kind: 'money' },
      {
        key: 'payouts_in_flight_minor',
        label: 'Payouts in flight',
        kind: 'money',
      },
      { key: 'checked_at', label: 'Checked', kind: 'datetime' },
    ],
  },
  {
    key: 'settlement',
    label: 'Settlement',
    action: 'money.settlement',
    description:
      'Top-ups credited to wallets and withdrawals paid, against the pool statement, matched by the bank reference.',
    filters: ['from', 'to'],
    columns: [
      col('business_date', 'Day', 'date'),
      col('kind', 'Money'),
      col('rail', 'Network'),
      col('n', 'Count', 'number'),
      col('sao_minor', "In Sao's books", 'money'),
      col('landed_minor', 'On statement', 'money'),
      col('difference_minor', 'Difference', 'money'),
      col('not_matched_n', 'Not on statement', 'number'),
      col('state', 'State', 'status'),
    ],
  },
  {
    key: 'cards',
    label: 'Cards',
    action: 'money.cards',
    description:
      'Every card top-up, and whether its money has reached the pool. Shows the last 14 days unless you pick dates.',
    filters: ['from', 'to', 'card_status', 'unsettled', 'show_test'],
    empty: 'No card payments in this period.',
    columns: [
      col('created_at', 'Paid', 'datetime'),
      col('reference', 'Reference', 'mono'),
      col('customer_id', 'Customer', 'customer'),
      col('amount_minor', 'Amount', 'money'),
      col('card_last4', 'Card', 'card'),
      col('status', 'Status', 'status'),
      col('core_status', 'SaoPay status'),
      col('credited_at', 'Credited', 'datetime'),
      col('settlement', 'Settlement', 'status'),
      col('statement_reference', 'Statement reference', 'mono'),
    ],
    sheetFields: [
      col('created_at', 'Paid', 'datetime'),
      col('reference', 'Reference', 'mono'),
      col('customer_id', 'Customer', 'customer'),
      col('amount_minor', 'Amount', 'money'),
      col('card_last4', 'Card', 'card'),
      col('status', 'Status', 'status'),
      col('core_status', 'SaoPay status'),
      col('core_environment', 'Environment'),
      col('credited_at', 'Credited', 'datetime'),
      col('settlement', 'Settlement', 'status'),
      col('settled_at', 'Settled', 'datetime'),
      col('statement_reference', 'Statement reference', 'mono'),
      col('transaction_id', 'Transaction', 'transaction'),
      col('core_order_id', 'SaoPay order id', 'mono'),
      col('core_session_id', 'SaoPay session', 'mono'),
      col('core_message', 'SaoPay message'),
    ],
  },
  {
    key: 'untraced',
    label: 'Not traced',
    action: 'money.untraced',
    description:
      'For one day: top-ups credited and withdrawals paid that no statement line carries, and statement credits no top-up accounts for. Defaults to yesterday.',
    filters: ['day'],
    empty: 'Everything matched on this day.',
    columns: [
      col('side', 'Where it is missing', 'status'),
      col('rail', 'Network'),
      col('bank_reference', 'Bank reference', 'mono'),
      col('sao_reference', 'Sao reference', 'mono'),
      col('statement_reference', 'Statement reference', 'mono'),
      col('amount_minor', 'Amount', 'money'),
      col('at', 'Credited', 'datetime'),
      col('transaction_id', 'Transaction', 'transaction'),
    ],
  },
  {
    key: 'statement',
    label: 'Pool statement',
    action: 'money.statement_lines',
    description: 'Lines from the bank statement of the pool account.',
    filters: ['day', 'class', 'unbooked'],
    columns: [
      { key: 'value_date', label: 'Value date', kind: 'date' },
      { key: 'direction', label: 'Direction' },
      { key: 'amount_minor', label: 'Amount', kind: 'money' },
      { key: 'reference', label: 'Reference', kind: 'mono' },
      { key: 'narration', label: 'Narration' },
      { key: 'class', label: 'Class', kind: 'status' },
      { key: 'booked_by', label: 'Booked by' },
      { key: 'running_balance_minor', label: 'Balance', kind: 'money' },
    ],
    // A card_settlement line is booked with its card payments (Cards tab), never with Book.
    rowActions: (row) =>
      !row.booked_by &&
      ['unexplained', 'charge', 'own_funds'].includes(String(row.class)) ? (
        <ProposeChange
          kind="pool_booking"
          payload={{
            line_id: String(row.id),
            as: row.direction === 'debit' ? 'charge' : 'own_funds',
          }}
          label="Book"
          title={
            row.direction === 'debit'
              ? 'Book this line as a bank charge'
              : "Book this line as Sao's own funds"
          }
          description="An unexplained pool line, booked so the pool matches the books."
          evidence={[{ type: 'statement_line', ref: String(row.id) }]}
        />
      ) : null,
  },
  {
    key: 'core',
    label: 'Core balances',
    action: 'money.core_balances',
    description: 'Checks of each core account against the books.',
    filters: ['show'],
    empty: 'Every account matched.',
    columns: [
      { key: 'created_at', label: 'When', kind: 'datetime' },
      { key: 'business_date', label: 'Day', kind: 'date' },
      { key: 'account_label', label: 'Account' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'result', label: 'Result', kind: 'status' },
      { key: 'core_minor', label: 'Core', kind: 'money' },
      { key: 'book_minor', label: 'Books', kind: 'money' },
      { key: 'difference_minor', label: 'Difference', kind: 'money' },
    ],
  },
  {
    key: 'provisioning',
    label: 'Provisioning',
    action: 'money.provisioning',
    description: 'Wallets whose account at the core has not been opened.',
    columns: [
      { key: 'provisioning_started_at', label: 'Started', kind: 'datetime' },
      { key: 'currency', label: 'Currency' },
      { key: 'customer_id', label: 'Customer', kind: 'customer' },
      { key: 'wallet_id', label: 'Wallet', kind: 'mono' },
      { key: 'provisioning_error', label: 'Error', kind: 'mono' },
    ],
  },
  {
    key: 'books',
    label: 'Books',
    action: 'money.books',
    description:
      'The trial balance for a day, the wallet balance cross-check and the ledger chain.',
    filters: ['day'],
    columns: [
      { key: 'currency', label: 'Currency' },
      { key: 'account_type', label: 'Account type' },
      { key: 'balance_minor', label: 'Balance', kind: 'money' },
    ],
  },
  {
    key: 'bills',
    label: 'Bills',
    action: 'money.bills',
    description: 'Bill payments against what Hubtel reported, up to 93 days.',
    filters: ['from', 'to', 'issues_only'],
    columns: [
      { key: 'created_at', label: 'When', kind: 'datetime' },
      { key: 'biller', label: 'Biller' },
      { key: 'status', label: 'Status', kind: 'status' },
      { key: 'provider_status', label: 'Hubtel status' },
      { key: 'amount_minor', label: 'Amount', kind: 'money' },
      { key: 'provider_amount_minor', label: 'Hubtel amount', kind: 'money' },
      { key: 'issue', label: 'Issue', kind: 'status' },
      { key: 'bill_reference', label: 'Reference', kind: 'mono' },
      { key: 'transaction_id', label: 'Transaction', kind: 'transaction' },
    ],
  },
  {
    key: 'fees',
    label: 'Fee income',
    action: 'money.fee_income',
    description: 'Fees earned and refunded per day, product and component.',
    filters: ['from', 'to'],
    columns: [
      { key: 'business_date', label: 'Day', kind: 'date' },
      { key: 'product', label: 'Product' },
      { key: 'component', label: 'Component' },
      { key: 'earned_minor', label: 'Earned', kind: 'money' },
      { key: 'refunded_minor', label: 'Refunded', kind: 'money' },
      { key: 'net_minor', label: 'Net', kind: 'money' },
    ],
  },
];

/** The Sweeps tab is its own page (a request and an approval, not a read-only table). */
const SWEEPS_TAB = {
  key: 'sweeps',
  label: 'Sweeps',
  description:
    'Hubtel’s money to the Access pool: what is owed, the checks, and each sweep.',
} as const;

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional();

const searchSchema = z.object({
  screen: z.string().max(20).catch('breaks').default('breaks'),
  view: z.enum(['sweeps', 'reports', 'policy']).optional().catch(undefined),
  from: day,
  to: day,
  day,
  status: z.string().max(20).optional(),
  kind: z.string().max(40).optional(),
  source: z.enum(['automatic', 'staff']).optional(),
  class: z.string().max(40).optional(),
  unbooked: z.boolean().optional(),
  issues_only: z.boolean().optional(),
  show: z.enum(['issues', 'all']).optional(),
  card_status: z.string().max(20).optional(),
  unsettled: z.boolean().optional(),
  show_test: z.boolean().optional(),
  zeros: z.boolean().optional(),
});

type MoneySearch = z.infer<typeof searchSchema>;

/** The money operations screens, read only. One tab per screen; filters in the URL. */
export const Route = createFileRoute('/_app/money')({
  validateSearch: searchSchema,
  component: MoneyPage,
});

function argsFor(screen: Screen, search: MoneySearch): Row {
  const args: Row = {};
  for (const filter of screen.filters ?? []) {
    const value = search[filter];
    if (value !== undefined) args[filter] = value;
  }
  if (screen.action === 'money.cards') {
    // The page's own filter names; the server's arguments are `status` and the rest.
    delete args.card_status;
    if (search.card_status) args.status = search.card_status;
  }
  if (screen.action === 'money.bills') {
    if (typeof args.from === 'string') args.from = `${args.from}T00:00:00Z`;
    if (typeof args.to === 'string') args.to = `${args.to}T23:59:59.999Z`;
    args.issues_only = search.issues_only ?? true;
  }
  if (screen.action === 'money.core_balances') {
    args.show = search.show ?? 'issues';
    if (search.show === 'all' && search.zeros) args.zeros = true;
  }
  return args;
}

/**
 * The tabs this cell has. Bills are paid and reported by Hubtel, and sweeps
 * move Hubtel's money to the Access pool, so a cell without Hubtel has neither.
 */
function visibleScreens(): readonly Screen[] {
  return SCREENS.filter((item) => item.key !== 'bills' || hasHubtel());
}

function visibleTabs() {
  return hasHubtel()
    ? [...visibleScreens(), SWEEPS_TAB]
    : [...visibleScreens()];
}

/** Inside Sweeps: the sweeps themselves, and the audit reports over them. */
const SWEEP_VIEWS = [
  { key: 'sweeps', label: 'Sweeps' },
  { key: 'reports', label: 'Reports' },
  { key: 'policy', label: 'Policy' },
] as const;

function MoneyPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onSweeps = search.screen === SWEEPS_TAB.key && hasHubtel();
  const screens = visibleScreens();
  const screen =
    screens.find((item) => item.key === search.screen) ??
    (screens[0] as Screen);
  const set = (patch: Partial<MoneySearch>) =>
    void navigate({ search: { ...search, ...patch } });
  const tabs = (
    <TabLinks
      tabs={visibleTabs()}
      active={onSweeps ? SWEEPS_TAB.key : screen.key}
      render={(tab, domProps) => (
        <Link {...domProps} to="/money" search={{ screen: tab.key }} />
      )}
    />
  );
  if (onSweeps) {
    return (
      <>
        <PageHeader title="Money" description={SWEEPS_TAB.description} />
        {tabs}
        <TabLinks
          tabs={SWEEP_VIEWS}
          active={search.view ?? 'sweeps'}
          render={(tab, domProps) => (
            <Link
              {...domProps}
              to="/money"
              search={{
                screen: 'sweeps',
                view: tab.key as 'sweeps' | 'reports',
              }}
            />
          )}
        />
        <SweepAuto />
        {search.view === 'reports' ? <SweepReports /> : null}
        {search.view === 'policy' ? <SweepPolicyView /> : null}
        {search.view === 'reports' || search.view === 'policy' ? null : (
          <SweepsPanel />
        )}
      </>
    );
  }
  return <MoneyScreen screen={screen} search={search} set={set} tabs={tabs} />;
}

/** One read-only screen: its rows, filters and the extras it answers beside them. */
function MoneyScreen({
  screen,
  search,
  set,
  tabs,
}: Readonly<{
  screen: Screen;
  search: MoneySearch;
  set: (patch: Partial<MoneySearch>) => void;
  tabs: ReactNode;
}>) {
  const args = argsFor(screen, search);
  // Screens that page (`next_before`) load more as the reader reaches the end.
  const list = useKeysetList(screen.action, args as never, {
    rows: (page) => ((page as Row).rows as Row[] | undefined) ?? [],
  });
  const filedRows = list.rows;
  useEffect(() => {
    fileMoneyRows(screen.key, filedRows as Row[] | undefined);
  }, [screen.key, filedRows]);

  return (
    <>
      <PageHeader
        title="Money"
        description={screen.description}
        actions={
          <>
            {screen.action === 'money.cards' ? <BookCardSettlement /> : null}
            <ExportButton source={screen.action} args={args} />
          </>
        }
      />
      {tabs}
      <Filters screen={screen} search={search} onChange={set} />
      {list.error ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      {list.data ? (
        <Extras action={screen.action} data={list.data as Row} />
      ) : null}
      <RecordTable
        columns={screen.columns}
        rows={list.rows}
        more={list.more}
        empty={coreEmpty(screen, args, list.data as Row | undefined)}
        renderActions={
          MONEY_SHEET_SCREENS.has(screen.key) ? undefined : screen.rowActions
        }
        sheetFor={(row) => moneySheetRef(screen.key, row)}
        openButtons={!MONEY_SHEET_SCREENS.has(screen.key)}
      />
    </>
  );
}

type FilterProps = Readonly<{
  screen: Screen;
  search: MoneySearch;
  onChange: (patch: Partial<MoneySearch>) => void;
}>;

function Field({ children }: Readonly<{ children: ReactNode }>) {
  return <div className="flex flex-col gap-1">{children}</div>;
}

function CheckFilter({
  name,
  label,
  fallback,
  search,
  onChange,
}: Readonly<{
  name: 'unbooked' | 'issues_only' | 'zeros' | 'unsettled' | 'show_test';
  label: string;
  fallback: boolean;
}> &
  Pick<FilterProps, 'search' | 'onChange'>) {
  return (
    <div className="pb-2">
      <Checkbox
        checked={search[name] ?? fallback}
        onCheckedChange={(checked) => onChange({ [name]: checked === true })}
      >
        {label}
      </Checkbox>
    </div>
  );
}

function DateFilter({
  name,
  label,
  search,
  onChange,
}: Readonly<{ name: 'from' | 'to' | 'day'; label: string }> &
  Pick<FilterProps, 'search' | 'onChange'>) {
  return (
    <Field>
      <DatePicker
        label={label}
        value={search[name] ?? ''}
        onChange={(event) =>
          onChange({ [name]: event.target.value || undefined })
        }
      />
    </Field>
  );
}

function StatusFilter({ search, onChange }: FilterProps) {
  return (
    <Field>
      <Select
        label="Status"
        className="w-full sm:w-36"
        value={search.status ?? 'open'}
        onChange={(event) => onChange({ status: event.target.value })}
      >
        {['open', 'resolved', 'all'].map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </Select>
    </Field>
  );
}

const CARD_STATUSES = [
  { value: 'credited', label: 'Credited' },
  { value: 'pending', label: 'Pending' },
  { value: 'requested', label: 'Requested' },
  { value: 'failed', label: 'Failed' },
  { value: 'not_completed', label: 'Not completed' },
  { value: 'review', label: 'In review' },
] as const;

function CardStatusFilter({ search, onChange }: FilterProps) {
  return (
    <Field>
      <Select
        label="Status"
        className="w-full sm:w-44"
        value={search.card_status ?? ''}
        onChange={(event) =>
          onChange({ card_status: event.target.value || undefined })
        }
      >
        <option value="">Any</option>
        {CARD_STATUSES.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function SourceFilter({ search, onChange }: FilterProps) {
  return (
    <Field>
      <Select
        label="By"
        className="w-full sm:w-36"
        value={search.source ?? ''}
        onChange={(event) =>
          onChange({
            source: (event.target.value || undefined) as
              | MoneySearch['source']
              | undefined,
          })
        }
      >
        <option value="">Anyone</option>
        <option value="automatic">Automatic</option>
        <option value="staff">Staff</option>
      </Select>
    </Field>
  );
}

function KindFilter({ screen, search, onChange }: FilterProps) {
  if (!screen.kinds) {
    return (
      <Field>
        <Label htmlFor="money-kind">Kind</Label>
        <Input
          id="money-kind"
          className="w-full sm:w-44"
          defaultValue={search.kind ?? ''}
          onBlur={(event) =>
            onChange({ kind: event.target.value || undefined })
          }
        />
      </Field>
    );
  }
  return (
    <Field>
      <Select
        label="Payment"
        className="w-full sm:w-44"
        value={search.kind ?? ''}
        onChange={(event) =>
          onChange({ kind: event.target.value || undefined })
        }
      >
        <option value="">Any</option>
        {screen.kinds.map((kind) => (
          <option key={kind.value} value={kind.value}>
            {kind.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function ClassFilter({ search, onChange }: FilterProps) {
  return (
    <Field>
      <Select
        label="Class"
        className="w-full sm:w-44"
        value={search.class ?? ''}
        onChange={(event) =>
          onChange({ class: event.target.value || undefined })
        }
      >
        <option value="">Any</option>
        {STATEMENT_CLASSES.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function ShowFilter({ search, onChange }: FilterProps) {
  return (
    <>
      <Field>
        <Select
          label="Show"
          className="w-full sm:w-44"
          value={search.show ?? 'issues'}
          onChange={(event) =>
            onChange({
              show: event.target.value === 'all' ? 'all' : 'issues',
              zeros: undefined,
            })
          }
        >
          <option value="issues">Only problems</option>
          <option value="all">All accounts</option>
        </Select>
      </Field>
      {search.show === 'all' ? (
        <CheckFilter
          name="zeros"
          label="Include empty accounts"
          fallback={false}
          search={search}
          onChange={onChange}
        />
      ) : null}
    </>
  );
}

/** Filter controls in display order, keyed by the name a screen lists. */
const FILTER_CONTROLS: Record<string, (props: FilterProps) => ReactNode> = {
  status: (p) => <StatusFilter {...p} />,
  card_status: (p) => <CardStatusFilter {...p} />,
  source: (p) => <SourceFilter {...p} />,
  kind: (p) => <KindFilter {...p} />,
  class: (p) => <ClassFilter {...p} />,
  day: (p) => <DateFilter name="day" label="Day" {...p} />,
  from: (p) => <DateFilter name="from" label="From" {...p} />,
  to: (p) => <DateFilter name="to" label="To" {...p} />,
  unbooked: (p) => (
    <CheckFilter
      name="unbooked"
      label="Not booked yet"
      fallback={false}
      {...p}
    />
  ),
  unsettled: (p) => (
    <CheckFilter
      name="unsettled"
      label="Not settled only"
      fallback={false}
      {...p}
    />
  ),
  show_test: (p) => (
    <CheckFilter
      name="show_test"
      label="Show test payments"
      fallback={false}
      {...p}
    />
  ),
  issues_only: (p) => (
    <CheckFilter
      name="issues_only"
      label="Only with an issue"
      fallback={true}
      {...p}
    />
  ),
  show: (p) => <ShowFilter {...p} />,
};

function Filters(props: FilterProps) {
  const filters = props.screen.filters ?? [];
  if (filters.length === 0) return null;
  return (
    <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
      {Object.entries(FILTER_CONTROLS)
        .filter(([name]) => filters.includes(name as never))
        .map(([name, render]) => (
          <Fragment key={name}>{render(props)}</Fragment>
        ))}
    </div>
  );
}

function BooksExtras({ data }: Readonly<{ data: Row }>) {
  const chain = data.chain as {
    chain_ok?: boolean;
    broken_at?: number | null;
  } | null;
  const all = (data.wallet_balance_issues as Row[]) ?? [];
  const faults = all.filter((row) => BOOKS_FAULTS.has(String(row.issue)));
  const inFlight = all.length - faults.length;
  const tbRows = (data.rows as Row[]) ?? [];
  const unbalanced = unbalancedCurrencies(tbRows);
  const day = typeof data.day === 'string' ? data.day : 'the day';
  return (
    <div className="flex flex-col gap-3">
      <Check
        ok={chain?.chain_ok !== false}
        good="Ledger chain intact: no entry has been changed or removed."
        bad={`The ledger chain is broken at entry ${String(chain?.broken_at)}.`}
      />
      {tbRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No trial balance for {day} yet. Pick an earlier day.
        </p>
      ) : (
        <Check
          ok={unbalanced.length === 0}
          good={`Trial balance for ${day} adds up.`}
          bad={`Trial balance for ${day} does not add up in ${unbalanced.join(', ')}.`}
        />
      )}
      <Check
        ok={faults.length === 0}
        good="All wallet balances match their entries."
        bad={`${faults.length} wallet balance${faults.length === 1 ? ' does' : 's do'} not match the entries.`}
      />
      {faults.length > 0 ? (
        <RecordTable
          columns={[
            { key: 'issue', label: 'Issue', kind: 'status' },
            { key: 'subject_id', label: 'Subject', kind: 'mono' },
            { key: 'detail', label: 'Detail', kind: 'json' },
          ]}
          rows={faults}
          sheetFor={booksIssueSheet}
        />
      ) : null}
      {inFlight > 0 ? (
        <p className="text-sm text-muted-foreground">
          {inFlight} item{inFlight === 1 ? ' is' : 's are'} still in flight
          (stuck payments, open breaks, approvals). They are on the Stuck and
          Breaks tabs.
        </p>
      ) : null}
    </div>
  );
}

function Check({
  ok,
  good,
  bad,
}: Readonly<{ ok: boolean; good: string; bad: string }>) {
  return (
    <Alert variant={ok ? 'success' : 'destructive'}>{ok ? good : bad}</Alert>
  );
}

/** Faults in the books themselves; everything else the check lists is work in flight. */
const BOOKS_FAULTS = new Set([
  'balance_mismatch',
  'held_mismatch',
  'fee_booking_mismatch',
  'trial_balance_unbalanced',
  'unbacked_money',
  'closed_with_balance',
]);

/** Currencies whose trial balance does not sum to zero. */
function unbalancedCurrencies(rows: readonly Row[]): string[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const currency = String(row.currency);
    totals.set(
      currency,
      (totals.get(currency) ?? 0) + Number(row.balance_minor),
    );
  }
  return [...totals].filter(([, total]) => total !== 0).map(([c]) => c);
}

function CoreExtras({ data }: Readonly<{ data: Row }>) {
  const summary = data.summary as CoreSummary | null | undefined;
  if (!summary?.business_date) return null;
  const bad = summary.mismatched > 0;
  return (
    <p
      className={
        bad ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'
      }
    >
      {coreDay(summary.business_date)}: {summary.checked} accounts checked ·{' '}
      {bad ? `${summary.mismatched} don't match` : 'all match'}
      {summary.pending > 0 ? ` · ${summary.pending} still pending` : ''}
    </p>
  );
}

interface HubtelSpend {
  paid_total_minor: number;
  charges_total_minor?: number;
  commission_total: number;
}

function SettlementExtras({ data }: Readonly<{ data: Row }>) {
  const status = useQuery({
    ...adminQuery('ops.status', {}),
    refetchInterval: 60_000,
  });
  const collection = useQuery({
    ...adminQuery('money.hubtel_collection', {}),
    refetchInterval: 60_000,
  });
  const totals = (data.totals as SettlementTotal[] | undefined) ?? [];
  const flow = settlementFlowNow(
    data.flow as SettlementFlow | undefined,
    (status.data as OpsStatus | undefined)?.live_pool,
  );
  const hubtel = data.hubtel as HubtelSpend | undefined;
  const through =
    typeof data.statement_through === 'string' ? data.statement_through : null;
  const float = (status.data as OpsStatus | undefined)?.hubtel_float;
  return (
    <div className="flex flex-col gap-3">
      {flow && hubtel ? (
        <SettlementSummary
          flow={flow}
          hubtel={{
            collections: collectionsText(collection.data, Date.now()),
            balance:
              float?.state === 'ok'
                ? formatMoney(float.amount_minor, float.currency)
                : status.isPending
                  ? 'Loading…'
                  : 'Unavailable',
            billsMinor: hubtel.paid_total_minor,
            chargesMinor: hubtel.charges_total_minor ?? 0,
            commission: hubtel.commission_total,
          }}
        />
      ) : null}
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <PoolRefresh scope="settlement" />
        <span>
          Reads the Access statement and the Hubtel collections balance now.
        </span>
      </div>
      {through ? (
        <p className="text-sm text-muted-foreground">
          Statement read through {through}. Later days show as awaiting the
          statement.
        </p>
      ) : null}
      <SettlementWarnings totals={totals} />
    </div>
  );
}

/**
 * The Access pool card from the live figures (the newest statement read and
 * today's books) when the server sends them, else the daily check's.
 */
function settlementFlowNow(
  flow: SettlementFlow | undefined,
  live: OpsStatus['live_pool'],
): SettlementFlow | undefined {
  if (!flow?.held || !live || live.currency !== flow.held.currency) return flow;
  return {
    ...flow,
    held: {
      ...flow.held,
      checked_at: live.statement_read_at ?? live.bank_as_of,
      bank_balance_minor: live.bank_balance_minor,
      wallets_minor: live.wallets_minor,
      cover_minor: live.cover_minor,
    },
  };
}

/** The Hubtel collections balance and how long ago it was read. */
function collectionsText(
  read:
    | { read: boolean; balance_minor?: number; checked_at?: string }
    | undefined,
  now: number,
): string {
  if (!read) return 'Loading…';
  if (!read.read || read.balance_minor == null || !read.checked_at) {
    return 'Not read yet';
  }
  const ago = durationText(ageMs(read.checked_at, now) ?? 0);
  return `${formatMoney(read.balance_minor, homeCurrency())} · ${ago} ago`;
}

function BillsExtras({ totals }: Readonly<{ totals: Row }>) {
  const t = totals as { bills: number; with_issue: number; paid_minor: number };
  return (
    <p className="text-sm text-muted-foreground">
      {t.bills} bills, {t.with_issue} with an issue,{' '}
      {formatMoney(t.paid_minor, homeCurrency())} paid.
    </p>
  );
}

/** What a screen answers beside its rows. */
function Extras({
  action,
  data,
}: Readonly<{ action: AdminAction; data: Row }>) {
  if (action === 'money.books') return <BooksExtras data={data} />;
  if (action === 'money.core_balances') return <CoreExtras data={data} />;
  if (action === 'money.settlement') return <SettlementExtras data={data} />;
  if (action === 'money.cards' && data.summary) {
    return <CardsSummary summary={data.summary as CardPaymentsSummary} />;
  }
  if (action === 'money.bills' && data.totals) {
    return <BillsExtras totals={data.totals as Row} />;
  }
  return null;
}

interface CoreSummary {
  business_date: string | null;
  checked: number;
  matched: number;
  mismatched: number;
  pending: number;
}

function coreDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** The empty line of a screen; the core checks say which day matched. */
function coreEmpty(screen: Screen, args: Row, data: Row | undefined): string {
  if (screen.action === 'money.core_balances' && args.show === 'issues') {
    const date = (data?.summary as CoreSummary | undefined)?.business_date;
    return date
      ? `Every account matched on ${coreDay(date)}.`
      : 'No checks yet.';
  }
  return screen.empty ?? 'Nothing here.';
}

/** The classes classify_pool_line gives a statement line. */
const STATEMENT_CLASSES = [
  { value: 'customer', label: 'Customer' },
  { value: 'charge', label: 'Bank charge' },
  { value: 'own_funds', label: 'Own funds' },
  { value: 'unexplained', label: 'Unexplained' },
  { value: 'card_settlement', label: 'Card settlement' },
] as const;

/** Opens the transaction a books issue is about: the subject of a stuck one, else the one its detail names. */
function booksIssueSheet(row: Row): SheetRef | undefined {
  const detail = (row.detail ?? {}) as Row;
  const tx =
    row.issue === 'stuck_transaction' ? row.subject_id : detail.transaction_id;
  return typeof tx === 'string' && tx ? { type: 'tx', id: tx } : undefined;
}
